/**
 * Utility functions for formatting values consistently across the application
 */

/**
 * Format a number as currency
 * @param value - The number to format
 * @param currency - Currency code (e.g., 'USD', 'VND')
 * @param locale - Optional locale (defaults to 'en-US')
 * @returns Formatted currency string
 */
export const formatCurrency = (
  value: number | string, 
  currency: string = 'USD', 
  locale: string = 'en-US'
): string => {
  if (value === null || value === undefined) return '—';
  
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // Handle special case for VND (no decimal places)
  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency,
    minimumFractionDigits: currency === 'VND' ? 0 : 1,
    maximumFractionDigits: currency === 'VND' ? 0 : 1
  };

  return new Intl.NumberFormat(locale, options).format(numericValue);
};

/**
 * Format a date string
 * @param dateString - ISO date string or Date object
 * @param format - Optional format (default: 'medium')
 * @param locale - Optional locale (defaults to 'en-US')
 * @returns Formatted date string
 */
export const formatDate = (
  dateString: string | Date | undefined, 
  format: 'short' | 'medium' | 'long' | 'full' = 'medium',
  locale: string = 'en-US'
): string => {
  if (!dateString) return '—';
  
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  
  const options: Intl.DateTimeFormatOptions = {};
  
  switch (format) {
    case 'short':
      options.year = 'numeric';
      options.month = '2-digit';
      options.day = '2-digit';
      break;
    case 'medium':
      options.year = 'numeric';
      options.month = 'short';
      options.day = 'numeric';
      break;
    case 'long':
      options.year = 'numeric';
      options.month = 'long';
      options.day = 'numeric';
      break;
    case 'full':
      options.year = 'numeric';
      options.month = 'long';
      options.day = 'numeric';
      options.weekday = 'long';
      break;
  }
  
  return new Intl.DateTimeFormat(locale, options).format(date);
};

/**
 * Format a number as percentage
 * @param value - The number to format
 * @param decimals - Number of decimal places
 * @param locale - Optional locale (defaults to 'en-US')
 * @returns Formatted percentage string
 */
export const formatPercent = (
  value: number | string,
  decimals: number = 2,
  locale: string = 'en-US'
): string => {
  if (value === null || value === undefined) return '—';
  
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(numericValue / 100);
};

/**
 * Format a number with thousand separators
 * @param value - The number to format
 * @param decimals - Number of decimal places
 * @param locale - Optional locale (defaults to 'en-US')
 * @returns Formatted number string
 */
export const formatNumber = (
  value: number | string,
  decimals: number = 0,
  locale: string = 'en-US'
): string => {
  if (value === null || value === undefined) return '—';
  
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(numericValue);
};
